
import React, { createContext, useContext, useEffect, useReducer, useCallback, useState } from 'react';
import { useAuth } from './AuthContext';
import { API } from '../lib/api';
import { toast } from 'sonner';

const CartContext = createContext();
const STORAGE_KEY = 'sdny_cart_v1';

// Helper functions for guest cart (localStorage)
function getGuestCart() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveGuestCart(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    console.warn('Failed to save cart to localStorage', e);
  }
}

function clearGuestCart() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

function cartReducer(state, action) {
  switch (action.type) {
    case 'INIT':
      return { items: action.items };
    case 'ADD': {
      const existing = state.items.find(i => i.key === action.item.key);
      if (existing) {
        // For portfolio/AI items (item_type 3,4), don't increase quantity - they're unique
        if (action.item.item_type === 3 || action.item.item_type === 4) {
          return state; // Already in cart
        }
        return {
          items: state.items.map(i => 
            i.key === action.item.key 
              ? { ...i, quantity: i.quantity + action.item.quantity } 
              : i
          )
        };
      }
      return { items: [...state.items, action.item] };
    }
    case 'UPDATE_QTY':
      return {
        items: state.items.map(i => 
          i.key === action.key ? { ...i, quantity: action.quantity } : i
        )
      };
    case 'REMOVE':
      return { items: state.items.filter(i => i.key !== action.key) };
    case 'SET_ITEMS':
      return { items: action.payload };
    case 'CLEAR':
      return { items: [] };
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const { user, token, loading: authLoading } = useAuth();
  const isAuthenticated = !!user && !!token;
  const [state, dispatch] = useReducer(cartReducer, { items: [] });
  const [loading, setLoading] = useState(true);

  // Fetch cart from server (reusable)
  const fetchServerCart = async () => {
    try {
      const response = await API.get('/cart');
      const serverItems = (response.data || []).map(item => ({
        ...item,
        key: (item.item_type === 3 || item.item_type === 4) 
          ? `portfolio_${item.product_id}` 
          : `${item.product_id}_${item.variant_id || ''}_${item.shape}_${item.metal_type}_${item.metal_color}_${item.ring_size}_${item.stone_quality}`
      }));
      dispatch({ type: 'INIT', items: serverItems });
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  // Refresh cart from server (public method)
  const refreshCart = async () => {
    if (isAuthenticated) {
      await fetchServerCart();
    }
  };

  // Load cart on mount or auth change
  useEffect(() => {
    if (authLoading) return;
    
    const loadCart = async () => {
      setLoading(true);
      
      if (isAuthenticated) {
        // User is logged in: sync guest cart then load from server
        try {
          const guestCart = getGuestCart();
          
          // Sync guest cart to server if it has items
          if (guestCart.length > 0) {
            await API.post('/cart/add', { items: guestCart });
            clearGuestCart();
          }
          
          // Fetch user's cart from server
          const response = await API.get('/cart');
          const serverItems = (response.data || []).map(item => ({
            ...item,
            // item_type: 1=product, 2=diamond, 3=portfolio, 4=AI generated
            key: (item.item_type === 3 || item.item_type === 4) 
              ? `portfolio_${item.product_id}` 
              : `${item.product_id}_${item.variant_id || ''}_${item.shape}_${item.metal_type}_${item.metal_color}_${item.ring_size}_${item.stone_quality}`
          }));
          
          dispatch({ type: 'INIT', items: serverItems });
        } catch (error) {
          console.error('Error loading user cart:', error);
          toast.error('Failed to load cart');
        } finally {
          setLoading(false);
        }
      } else {
        // Guest user: load from localStorage
        try {
          const guestCart = getGuestCart();
          const itemsWithKeys = guestCart.map(item => ({
            ...item,
            key: item.key || `${item.product_id}_${item.variant_id || ''}_${item.shape}_${item.metal_type}_${item.metal_color}_${item.ring_size}_${item.stone_quality}`
          }));
          dispatch({ type: 'INIT', items: itemsWithKeys });
        } catch (error) {
          console.error('Error loading guest cart:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadCart();
  }, [isAuthenticated, authLoading]);

  // Persist guest cart to localStorage when it changes
  useEffect(() => {
    if (!authLoading && !isAuthenticated && state.items.length >= 0) {
      saveGuestCart(state.items);
    }
  }, [state.items, isAuthenticated, authLoading]);

  // Add item to cart - NO OPTIMISTIC UI, backend is source of truth
  const addItem = useCallback(async (data) => {
    // Handle portfolio/AI items differently (item_type 3=portfolio, 4=AI generated)
    if (data.item_type === 3 || data.item_type === 4) {
      const key = `portfolio_${data.product_id}`;
      const item = { 
        ...data, 
        quantity: 1, 
        key, 
        id: key
      };
      
      // Check if already in cart
      const existing = state.items.find(i => i.key === key);
      if (existing) {
        toast.info('This item is already in your cart');
        return;
      }
      
      if (isAuthenticated) {
        // Send portfolio item to backend
        try {
          const response = await API.post('/cart', item);
          
          if (response.status === 200 || response.status === 201) {
            // Re-fetch to ensure sync with server
            const cartResponse = await API.get('/cart');
            const serverItems = (cartResponse.data || []).map(i => ({
              ...i,
              key: (i.item_type === 3 || i.item_type === 4) 
                ? `portfolio_${i.product_id}` 
                : `${i.product_id}_${i.variant_id || ''}_${i.shape}_${i.metal_type}_${i.metal_color}_${i.ring_size}_${i.stone_quality}`
            }));
            dispatch({ type: 'INIT', items: serverItems });
          } else {
            throw new Error(response.data?.detail || 'Failed to add to cart');
          }
        } catch (error) {
          console.error('Error adding portfolio item to cart:', error);
          throw error;
        }
      } else {
        // Guest user - store in localStorage
        dispatch({ type: 'ADD', item });
      }
      return;
    }
    
    const key = `${data.product_id}_${data.variant_id || ''}_${data.shape}_${data.metal_type}_${data.metal_color}_${data.ring_size}_${data.stone_quality}`;
    const item = { ...data, quantity: data.quantity || 1, key, id: data.id || key };
    
    if (isAuthenticated) {
      // Server-side cart - wait for backend confirmation before updating state
      try {
        const response = await API.post('/cart', item);
        
        // Only update state after successful API response
        if (response.status === 200 || response.status === 201) {
          // Re-fetch to ensure sync with server
          const cartResponse = await API.get('/cart');
          const serverItems = (cartResponse.data || []).map(i => ({
            ...i,
            key: (i.item_type === 3 || i.item_type === 4) 
              ? `portfolio_${i.product_id}` 
              : `${i.product_id}_${i.variant_id || ''}_${i.shape}_${i.metal_type}_${i.metal_color}_${i.ring_size}_${i.stone_quality}`
          }));
          dispatch({ type: 'INIT', items: serverItems });
        } else {
          throw new Error(response.data?.detail || 'Failed to add to cart');
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        // Re-throw so caller can handle error
        throw error;
      }
    } else {
      // Guest cart (localStorage)
      dispatch({ type: 'ADD', item });
    }
  }, [isAuthenticated]);

  // Update quantity - NO OPTIMISTIC UI, backend is source of truth
  const updateQuantity = useCallback(async (key, quantity) => {
    if (quantity <= 0) {
      return removeItem(key);
    }
    
    if (isAuthenticated) {
      // Server-side cart - wait for backend confirmation
      try {
        const item = state.items.find(i => i.key === key);
        if (!item) {
          throw new Error('Item not found in cart');
        }
        
        const response = await API.patch(`/cart/${item.id}`, { quantity });
        
        // Only update state after successful API response
        if (response.status === 200) {
          dispatch({ type: 'UPDATE_QTY', key, quantity });
        } else {
          throw new Error(response.data?.detail || 'Failed to update quantity');
        }
      } catch (error) {
        console.error('Error updating quantity:', error);
        // Re-throw so caller can handle
        throw error;
      }
    } else {
      // Guest cart
      dispatch({ type: 'UPDATE_QTY', key, quantity });
    }
  }, [isAuthenticated, state.items]);

  // Remove item - NO OPTIMISTIC UPDATE, NO TOASTS (caller handles)
  const removeItem = useCallback(async (key) => {
    if (isAuthenticated) {
      // Server-side cart - wait for API confirmation
      const itemToRemove = state.items.find(i => i.key === key);
      if (!itemToRemove) {
        throw new Error('Item not found in cart');
      }
      
      try {
        // Call API and wait for response
        const response = await API.delete(`/cart/${itemToRemove.id}`);
        
        // Only update state after successful API response
        if (response.status === 200) {
          dispatch({ type: 'REMOVE', key });
        } else {
          throw new Error(response.data?.detail || 'Failed to remove item');
        }
      } catch (error) {
        console.error('Error removing item:', error);
        // Re-throw so caller can handle error and show toast
        throw error;
      }
    } else {
      // Guest cart - update localStorage
      dispatch({ type: 'REMOVE', key });
      saveGuestCart(state.items.filter(i => i.key !== key));
    }
  }, [isAuthenticated, state.items]);

  // Clear cart - NO TOASTS (caller handles)
  const clearCart = useCallback(async () => {
    if (isAuthenticated) {
      try {
        const response = await API.delete('/cart/clear');
        // Only clear state after successful API response
        if (response.status === 200 && response.data?.success) {
          dispatch({ type: 'CLEAR' });
        } else {
          throw new Error('Failed to clear cart');
        }
      } catch (error) {
        console.error('Error clearing cart:', error);
        // Re-throw so caller can handle
        throw error;
      }
    } else {
      // Guest cart - clear localStorage
      dispatch({ type: 'CLEAR' });
      clearGuestCart();
    }
  }, [isAuthenticated]);

  // Remove specific items (for checkout) - NO TOASTS (caller handles)
  const removeMultipleItems = useCallback(async (itemKeys) => {
    if (isAuthenticated) {
      try {
        // Remove items one by one from backend using item ID
        const itemsToRemove = state.items.filter(item => itemKeys.includes(item.key));
        
        // Wait for all deletions to complete
        const results = await Promise.allSettled(
          itemsToRemove.map(item => API.delete(`/cart/${item.id}`))
        );
        
        // Check if any failed
        const failures = results.filter(r => r.status === 'rejected');
        if (failures.length > 0) {
          throw new Error(`Failed to remove ${failures.length} item(s)`);
        }
        
        // Only update state after all successful deletions
        dispatch({ 
          type: 'SET_ITEMS', 
          payload: state.items.filter(item => !itemKeys.includes(item.key)) 
        });
      } catch (error) {
        console.error('Error removing items:', error);
        // Re-throw so caller can handle
        throw error;
      }
    } else {
      // Guest cart: filter out the items
      const updatedItems = state.items.filter(item => !itemKeys.includes(item.key));
      dispatch({ type: 'SET_ITEMS', payload: updatedItems });
      saveGuestCart(updatedItems);
    }
  }, [isAuthenticated, state.items]);

  const totalItems = state.items.reduce((sum, i) => sum + (i.quantity || 1), 0);
  const subtotal = state.items.reduce((sum, i) => {
    // Handle different item types - portfolio items don't have a price
    if (i.type === 'portfolio') return sum;
    const itemPrice = i.price;
    return sum + ((itemPrice || 0) * (i.quantity || 1));
  }, 0);
  
  // Separate cart items by type
  const productItems = state.items.filter(i => i.type !== 'portfolio' && i.type !== 'diamond');
  const diamondItems = state.items.filter(i => i.type === 'diamond');
  const portfolioItems = state.items.filter(i => i.type === 'portfolio');

  return (
    <CartContext.Provider value={{ 
      items: state.items, 
      loading, 
      addItem, 
      updateQuantity, 
      removeItem, 
      clearCart,
      removeMultipleItems,
      refreshCart,
      totalItems, 
      subtotal,
      productItems,
      diamondItems,
      portfolioItems
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
